import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import debounce from "../utils/debounce";

export default class HomepageScrollingExperience {
  constructor(elem, APP) {
    this.elem = elem;
    this.slides = gsap.utils.toArray(this.elem.querySelectorAll("section, .tick-marks-divider"));
    this.slidesHeight = 0;
    this.tls = [];
    this.deviceType = null;
    this.previousDeviceType = null;
    this.APP = APP;
    this.tickmarks = this.elem.querySelector('.tick-marks-divider');
  }

  mobileAnimation() {
    let panels = this.slides;

    panels.forEach((panel, i) => {
      // console.log(panel, panel.offsetTop)
      this.slidesHeight += panel.offsetHeight;
      const componentName = panel.classList[0];
      const tl = ScrollTrigger.create({
        trigger: panel,
        start: () => {
          if (panel.classList.contains('homepageHero')) {
            return "top top";
          } else {
            return panel.offsetHeight < window.innerHeight ? "top top" : "bottom bottom"
          }
        },
        end: () => (i === panels.length - 1) ? "bottom bottom" : null,
        onUpdate: ({ progress, direction, isActive }) => {
          if (progress > 0.01) {
            panel.classList.add(`${componentName}--is-scrolled`);
          } else {
            panel.classList.remove(`${componentName}--is-scrolled`);
          }

          if (isActive) {
            const componentSkin = panel.dataset.navSkin;
            this.APP.header.setSkin(componentSkin);
          }


        },
        pin: true,
        pinSpacing: false,
        scrub: false,
        // markers: true,
      });

      this.tls.push(tl);
    });
  }

  desktopAnimation() {
    this.slides.forEach((slide, i) => {
      const componentName = slide.classList[0];
      const slideHeight = slide.offsetHeight;



      // global timeline
      const slideTl = gsap.timeline({
        scrollTrigger: {
          trigger: slide,
          start: () => {
            switch (componentName) {
              case 'homepageHero':
                return "top top";
              case 'footerPromo':
                return "bottom bottom";
              default:
                return slideHeight < window.innerHeight ? "top top" : "bottom bottom"
            }
          },
          end: () => {
            switch (componentName) {
              case 'footerPromo':
                return "bottom bottom";
              default:
                return null
            }
          },
          onToggle: ({ isActive }) => {
            // slide.classList.toggle(`${componentName}--is-scrolled`, isActive)
          },
          onUpdate: ({ progress }) => {
            switch (componentName) {
              case 'homepageHero':
                const hasClass = slide.classList.contains('homepageHero--is-scrolled');

                if (progress > 0.1 && !hasClass) {
                  slide.classList.add('homepageHero--is-scrolled');
                  this.APP.header.setSkin('dark');
                } else if (progress < 0.1 && hasClass && window.scrollY < slideHeight/2) {
                  slide.classList.remove('homepageHero--is-scrolled');
                  this.APP.header.setSkin('light');
                }
                break;
              default:
                const componentSkin = slide.dataset.navSkin;
                this.APP.header.setSkin(componentSkin);
                break;
            }
          },
          scrub: true,
          pin: true,
          pinSpacing: false,
          // markers: {
          //   startColor: "blue",
          //   endColor: "red",
          //   indent: 20,
          // }
        },
        // resize: () => {
        //   console.log('resize')
        // }
      });

      // vcarousel and featured-stats animation
      if (componentName == 'vcarousel' || componentName === 'featured-stats') {
        // animation to the border radius of the slide
        const borderAnimation = gsap.fromTo(slide, {
          borderTopRightRadius: 50,
        }, {
          scrollTrigger: {
            trigger: slide,
            start: 'top 40%',
            end: 'top top',
            scrub: true,
            invalidateOnRefresh: true,
          },
          borderTopRightRadius: 0,
        });

        slideTl.add(borderAnimation);

        // animation to the width of the slide
        const widthAnimation = gsap.fromTo(slide, {
          x: () => -(slide.getBoundingClientRect().width / 2),
        }, {
          scrollTrigger: {
            trigger: slide,
            start: 'top bottom',
            end: 'top top',
            scrub: true,
            // markers: {
            //   startColor: "yellow",
            //   endColor: "orange",
            //   indent: 50,
            // },
            invalidateOnRefresh: true,
          },
          x: 0,
        });

        slideTl.add(widthAnimation);

        // animation to fade in the content of the slide
        const content = slide.querySelector('.container');
        const contentAnimation = gsap.from(content, {
          scrollTrigger: {
            trigger: slide,
            start: 'top 75%',
            end: 'top top',
            scrub: true,
            invalidateOnRefresh: true,
          },
          opacity: 0,
          translateY: 150,
        });

        slideTl.add(contentAnimation);
      }

      // tick marks animation
      if (componentName === 'tick-marks-divider') {
        const tickMarksWidthAnimation = gsap.fromTo(slide, {
          width: '45%',
          opacity: 0,
        }, {
          scrollTrigger: {
            trigger: slide,
            start: 'top bottom',
            end: 'top top',
            scrub: true,
            invalidateOnRefresh: true,
          },
          width: '100%',
          opacity: 1,
        });

        slideTl.add(tickMarksWidthAnimation);
      }

      this.tls.push(slideTl);
    });
  }

  destroyTls() {
    if (!this.tls.length) return;
    this.tls.forEach((tl) => {
      tl.kill();
    });
    this.tls = [];
  }


  deviceEventController() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (isMobile) {
      this.deviceType = 'mobile';
      if (this.previousDeviceType === this.deviceType) return;
      // mobile setup, desktop teardown
      this.destroyTls();
      this.mobileAnimation();
    }
    if (isDesktop) {
      this.deviceType = 'desktop';
      if (this.previousDeviceType === this.deviceType) return;
      // desktop setup, mobile teardown
      this.destroyTls();
      this.desktopAnimation();
    }

    this.previousDeviceType = isMobile ? 'mobile' : 'desktop';
  }


  init() {
    window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    this.deviceEventController();
    window.addEventListener('resize', debounce(this.deviceEventController.bind(this), 200));
  }
}
